import { Fragment, useEffect, useMemo, useState } from 'react'

import {
  IconAdjustmentsHorizontal,
  IconCaretDown,
  IconPlus,
  IconSearch,
} from '@tabler/icons-react'
import { Form, Skeleton } from 'antd'
import { lowerCase } from 'lodash'
import styled from 'styled-components'

import { FILTER_KEY_PROP } from '../shared-components/Filters/_filterProps'

import { CorePageListViewFilterModal } from './CorePageListViewFilterModal'
import { CorePageListViewSort, SortOption } from './CorePageListViewSort'

import { Input } from '@/components/shared-components/BUI/Input'
import { FilterObjectType, useFilters } from '@/context/filterContext'
import { useQueryParams } from '@/context/queryParamsContext'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { onFormChangeDebounce } from '@/utils/onFormChange'

import { buildPassTheme } from 'src/styles/theme'

type Filter = FilterObjectType['filters']

export const Wrapper = styled.div`
  display: flex;
  align-items: center;
  background-color: ${({ theme }) => theme.colour.neutral.white};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: 1.25rem;
  margin-bottom: 0.5rem;
`

const SearchWrapper = styled.div`
  flex-shrink: 0;
  max-width: 88px;
  transition: max-width 0.4s;

  &:focus-within {
    max-width: 200px;
  }
`

const SectionDivider = styled.div`
  align-self: stretch;
  width: 2px;
  background-color: ${({ theme }) => theme.colour.secondary._50};
  margin-left: 1.125rem;
  margin-right: 1.125rem;
`

const ActiveFilters = styled.div`
  position: relative;
  ${({ theme }) => theme.font.label02}
  color: ${({ theme }) => theme.colour.secondary._600};
  padding-right: 1.5rem; // room for white gradient
  display: flex;
  align-items: center;
  text-transform: capitalize;
  overflow: hidden;
  white-space: nowrap;
  cursor: pointer;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 1.5rem;
    height: 100%;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 1) 100%
    );
  }
`

const Filter = styled.span`
  padding-left: 0.5rem;
  padding-right: 0.25rem;

  &:hover {
    color: ${({ theme }) => theme.colour.secondary.base};
    border-radius: ${({ theme }) => theme.borderRadius.max};
  }
`

const MoreFilters = styled.div`
  flex-shrink: 0;
  position: relative;
  ${({ theme }) => theme.font.label02}
  font-weight: 600;
  color: ${({ theme }) => theme.colour.secondary.base};
  display: flex;
  align-items: center;
  gap: 0.125rem;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colour.secondary._900};
  }
`

const FilterCount = styled.div`
  ${({ theme }) => theme.font.caption01}
  font-size: 0.5rem;
  position: absolute;
  top: -0.5rem;
  right: -1rem;
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colour.secondary.base};
  color: ${({ theme }) => theme.colour.neutral.white};
  display: flex;
  align-items: center;
  justify-content: center;
`

const IconWrapper = styled(MoreFilters)`
  margin-right: 0.5rem;
`

const SortWrapper = styled.div`
  flex-shrink: 0;
  padding-left: 0.5rem;
  margin-left: auto;

  ${({ theme: { media } }) => media.sm`
    padding-left: 2.5rem;
  `}
`

const getFilterKey = (filter: React.ReactNode) => {
  return filter?.['props']?.[FILTER_KEY_PROP]
}

const getFilterLabelOverrides = (
  filters: Array<React.ReactNode | React.ReactNode[]>
) => {
  if (!filters) return {}

  return filters.reduce<Record<string, string>>((acc, filter) => {
    if (Array.isArray(filter)) {
      filter.forEach((f) => {
        const key = getFilterKey(f)
        const override = filter?.['props']?.['filterLabelOverride']
        if (key && override) acc[key] = override
      })
    } else {
      const key = getFilterKey(filter)
      const override = filter?.['props']?.['filterLabelOverride']
      if (key && override) acc[key] = override
    }
    return acc
  }, {})
}

const getFilters = (
  filters: Filter,
  filterKeys: string[],
  labelOverrides: Record<string, string> = {}
) => {
  if (!filters) return null

  return Object.keys(filters).reduce((acc, filterKey) => {
    const value = filters[filterKey]
    const clone = [...acc]

    if (!filterKeys.includes(filterKey)) {
      return clone
    }

    const formattedKey = labelOverrides[filterKey] || lowerCase(filterKey)

    if (Array.isArray(value)) {
      if (value.length === 0) return clone

      if (value.length === 1) {
        if (typeof value[0] === 'boolean') {
          clone.push(formattedKey)
          return clone
        }
      }

      clone.push(`${formattedKey} (${value.length})`)
      return clone
    }

    clone.push(formattedKey)
    return clone
  }, [] as string[])
}

type Props = {
  loading?: boolean
  searchable?: boolean
  searchFilterKey?: string
  filters?: Array<React.ReactNode | React.ReactNode[]>
  sortOptions?: SortOption[]
  useQuery?: boolean
}

export const CorePageListViewFilterBar: React.FC<Props> = ({
  filters,
  searchable,
  searchFilterKey,
  sortOptions,
  loading,
  useQuery,
}) => {
  const isMobile = useBreakpoint('sm')

  const { params, updateParams } = useQueryParams()
  const { routeFilters, updateFilters } = useFilters()

  const [filterModalOpen, toggleFilterModal] = useState(false)
  const [form] = Form.useForm()

  const filterKeys = useMemo(() => {
    if (!filters) return []

    return filters.reduce<string[]>((acc, filter) => {
      return Array.isArray(filter)
        ? [...acc, ...filter.map(getFilterKey)]
        : [...acc, getFilterKey(filter)]
    }, [])
  }, [filters])

  const labelOverrides = useMemo(() => {
    return getFilterLabelOverrides(filters || [])
  }, [filters])

  const activeFilters = useMemo(() => {
    if (useQuery) {
      return getFilters(params, filterKeys, labelOverrides)
    }

    return getFilters(routeFilters?.filters, filterKeys, labelOverrides)
  }, [routeFilters?.filters, filterKeys, params, useQuery, labelOverrides])

  const persistedSearch = useMemo(() => {
    if (useQuery) {
      return params?.[searchFilterKey] as string
    }

    return routeFilters?.filters?.[searchFilterKey]?.[0] as string
  }, [routeFilters?.filters, searchFilterKey, params, useQuery])

  useEffect(() => {
    // sync the search value with the persisted search value comes into effect when "clearing" filters and such
    const currentSearch = form.getFieldValue('search')
    if (currentSearch !== persistedSearch) {
      form.setFieldValue('search', persistedSearch)
    }
  }, [persistedSearch, searchFilterKey, form])

  if (loading) {
    return (
      <Wrapper>
        <SearchWrapper>
          <Skeleton.Input
            active
            size="small"
            style={{ width: '88px', minWidth: '88px' }}
          />
        </SearchWrapper>
        <SectionDivider />
        <Skeleton.Input size="small" active />
      </Wrapper>
    )
  }

  const hasFilters = filters?.length > 0

  if (!searchable && !hasFilters) return null

  const openModal = () => toggleFilterModal(true)
  const closeModal = () => toggleFilterModal(false)

  const onSearchChange = (values: Record<string, string>) => {
    if (useQuery) {
      updateParams({ [searchFilterKey]: values.search })
    } else {
      const search = { [searchFilterKey]: [values.search as string] }
      updateFilters({ filters: { ...routeFilters.filters, ...search } })
    }
  }

  return (
    <>
      <Wrapper>
        {searchable && (
          <>
            <SearchWrapper>
              <Form
                form={form}
                onValuesChange={onFormChangeDebounce(form, onSearchChange, 750)}
              >
                <Form.Item name="search" noStyle>
                  <Input
                    naked
                    placeholder="Search..."
                    bordered={false}
                    prefix={<IconSearch size={16} />}
                  />
                </Form.Item>
              </Form>
            </SearchWrapper>
            <SectionDivider />
          </>
        )}

        {hasFilters && (
          <>
            <IconWrapper onClick={openModal}>
              <IconAdjustmentsHorizontal size={16} />
            </IconWrapper>

            {!isMobile && activeFilters?.length > 0 && (
              <ActiveFilters onClick={openModal}>
                {activeFilters.map((filter) => (
                  <Fragment key={filter}>
                    <Filter>{filter}</Filter>
                    <IconCaretDown
                      size={14}
                      fill={buildPassTheme.colour.secondary._500}
                      style={{ flexShrink: 0 }}
                    />
                  </Fragment>
                ))}
              </ActiveFilters>
            )}

            <MoreFilters onClick={openModal}>
              {!isMobile && !!activeFilters?.length && <IconPlus size={16} />}
              Filters
              {isMobile && !!activeFilters?.length && (
                <FilterCount>{activeFilters.length}</FilterCount>
              )}
            </MoreFilters>
          </>
        )}

        {sortOptions?.length > 0 && (
          <SortWrapper>
            <CorePageListViewSort
              sortOptions={sortOptions}
              isMobile={isMobile}
              useQuery={useQuery}
            />
          </SortWrapper>
        )}
      </Wrapper>

      <CorePageListViewFilterModal
        open={filterModalOpen}
        onClose={closeModal}
        filters={filters}
        useQuery={useQuery}
      />
    </>
  )
}
